import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, take, takeUntil } from 'rxjs';
import { HttpErrorHandlerService } from 'src/app/services/http-error-handler.service';
import { environment } from '../../../environments/environment';
import { StudyFile } from '../../models/Study/study-file.model';
import { Study } from '../../models/Study/study.model';
import { MediaGroup } from '../../models/media/media-group.model';
import { AuthService } from '../../services/auth.service';
import { RouteParamsService } from '../../services/route-param.service';
import { ViewerService } from '../../services/viewer.service';
import { BaseUnsubscribeComponent } from '../../shared-new/components/base-unsubscribe/base-unsubscribe.component';
import { StudyMimeType } from '../../shared-new/enums/study-mime-type.enum';
import { ViewMode } from '../../shared-new/enums/view-mode.emun';
import { StudyService } from '../../shared-new/services/study.service';
import { ToastService } from '../../shared-new/services/toast.service';
import { StudiesFacade } from '../../shared-new/store/studies/studies.facade';
import { DateParserFormatter } from '../../utils/helper/date-parser-formatter';
import { DownloadUtilsService } from 'src/app/services/download-utils-service';

@Component({
  selector: 'app-shared-clinical-data',
  templateUrl: './shared-clinical-data.component.html',
  styleUrls: ['./shared-clinical-data.component.scss']
})

export class SharedClinicalDataComponent extends BaseUnsubscribeComponent
  implements OnInit {
  viewMode = ViewMode.IMAGES;
  ViewMode = ViewMode;

  public study: Study;
  public studyFiles: StudyFile[];
  public dateFormat: string = '';
  public timeZone: string = '';
  mediaGroups$ = this.studyFacade.mediaGroupsAll$;
  public mediaGroups: MediaGroup[];
  public dicomFiles: any[] = [];
  public sortOrder = true;
  public currentSortColumn = '';
  public sanitizedUrl: SafeResourceUrl = null;
  public baseDicomUrl = environment.dicomViewer;
  public isPageExpire = false;

  constructor(
    private modalService: NgbModal,
    private studyFacade: StudiesFacade,
    private route: ActivatedRoute,
    private viewerService: ViewerService,
    private authService: AuthService,
    private readonly router: Router,
    private readonly activeRoute: ActivatedRoute,
    private dateParserFormatter: DateParserFormatter,
    private httpErrorHandler: HttpErrorHandlerService,
    private toastService: ToastService,
    private studyService: StudyService,
    private routeParamsService: RouteParamsService,
    private domSanitizer: DomSanitizer,
    private downloadUtilsService: DownloadUtilsService,
  ) {
    super();
  }


  private protectedId: string = null;
  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      const encryptedId = params.get('id');
      this.protectedId = encryptedId;

      this.authService.anonymousLogin(encryptedId).pipe(take(1)).subscribe((response) => {
        const url = `${this.baseDicomUrl}/?share=${response.hashKey}&token=${response.accessToken}`;
        this.renderDicomViewer(url);
        if (response.defaultAccessSection == "xrays") {
          this.setViewMode(ViewMode.IMAGES);
        } else if (response.defaultAccessSection == "media") {
          this.setViewMode(ViewMode.MEDIA);
        } else {
          this.setViewMode(ViewMode.REPORTS);
        }
        this.init(encryptedId);
      }, (error) => {
        this.httpErrorHandler.ShowErrorMessage(error);
      });
    });
  }

  private init(encryptedId: string) {
    this.studyFacade.loadSharedStudyFiles(encryptedId);
    this.dateFormat = this.dateParserFormatter.getDefaultDateTimeFormat(true);


    this.attachEvents(encryptedId);
  }

  private renderDicomViewer(url: string) {
    this.sanitizedUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(
      url
    );
  }

  private loadStudyFiles() {
    this.studyFacade.sharedStudyFiles$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response) => {
        this.studyFiles = response;
        setTimeout(() => {
          this.sortTable(this.currentSortColumn, true)
        })
        this.setViewMode(this.viewMode ?? ViewMode.IMAGES);
      });

    /*  this.studyFacade.selectShareStudyUID$.pipe((takeUntil(this.unsubscribe$))).subscribe((response) => {
      
    }); */
  }

  private attachEvents(encryptedId: string): void {
    this.loadStudyFiles();
    this.studyService
      .startValidation(encryptedId)
      .subscribe({
        next: (isValid) => {
          if (!isValid) {
            this.expirePage();
          }
        },
        error: (err) => {
          this.expirePage();
        },
      });
  }

  private expirePage() {
    this.isPageExpire = true;
    this.toastService.persistentError("This study share has expired, if you still want to view it, please contact the sender.", "Error");
  }

  setViewMode(mode: any): void {
    if (mode === ViewMode.IMAGES) {
      this.dicomFiles = this.getDicomFiles();
    }

    if (mode === ViewMode.MEDIA) {
      this.dicomFiles = this.getScopeFiles();
    }

    if (mode === ViewMode.REPORTS) {
      this.dicomFiles = this.getReportFiles();
    }

    this.viewMode = mode;
  }

  getDicomFiles() {
    const dicomFiles = this.studyFiles
      ?.filter((file) => file.studyFileMimeTypeId === StudyMimeType.XRAY)
      .sort((a, b) => {
        const dateA = new Date(a.createdDate).getTime();
        const dateB = new Date(b.createdDate).getTime();
        return dateA - dateB;
      });

    return dicomFiles;
  }

  getScopeFiles() {
    const dicomFiles = this.studyFiles
      ?.filter((file) => file.studyFileMimeTypeId === StudyMimeType.SCOPE_VIDEO)
      .sort((a, b) => {
        const dateA = new Date(a.createdDate).getTime();
        const dateB = new Date(b.createdDate).getTime();
        return dateA - dateB;
      });

    return dicomFiles;
  }

  getReportFiles() {
    const dicomFiles = this.studyFiles
      ?.filter((file) => file.studyFileMimeTypeId === StudyMimeType.XRAY_REPORT)
      .sort((a, b) => {
        const dateA = new Date(a.createdDate).getTime();
        const dateB = new Date(b.createdDate).getTime();
        return dateA - dateB;
      });

    return dicomFiles;
  }

  viewStudy(): void {
    this.viewerService.openDicomViewerForStudy(this.study);
  }

  viewScope(studyFile: StudyFile): void {
    this.viewerService.playVideo(this.study, studyFile);
  }

  viewPdf(studyFile: StudyFile): void {
    this.viewerService.openPdf(this.study, studyFile);
  }

  openXrayDownloaderPopup(): void {
    const xrayFiles = this.getDicomFiles();
    this.downloadUtilsService.triggerDownloaderPopup(null, xrayFiles, this.getReportFiles(), this.getScopeFiles(), 'share', this.protectedId);
  }
  /*
    openVideoDownloader(studyFileIds: number[]): void {
      const file = this.mp4StudyFiles.filter(x => studyFileIds.includes(x.studyFileId));
      if (file.length > 0) {
        this.downloadUtilsService.triggerFileDownload(file[0].thumbURL, file[0].originalFileName);
      }
    }
  
    openDocDownloader(studyFileIds: number[]): void {
      const file = this.pdfStudyFiles.filter(x => studyFileIds.includes(x.studyFileId));
      if (file.length > 0) {
        this.downloadUtilsService.triggerFileDownload(file[0].thumbURL, file[0].originalFileName);
      }
    } */

  public downloadStudyData() {
    this.openXrayDownloaderPopup();
  }

  public sortTable(column: string, isRefersh: boolean = false) {
    if (!this.dicomFiles || this.dicomFiles.length === 0) {
      return; // Return early if sortedDicomFiles is empty
    }
    if (!isRefersh) {
      if (this.currentSortColumn === column) {
        this.sortOrder = !this.sortOrder; // Toggle sorting order if the same column is clicked
      } else {
        this.sortOrder = true; // Default to ascending order when a new column is clicked
        this.currentSortColumn = column;
      }
    }

    this.dicomFiles.sort((a, b) => {
      const aValue = this.resolvePath(a, column);
      const bValue = this.resolvePath(b, column);

      if (aValue instanceof Date && bValue instanceof Date) {
        // Compare dates
        return this.sortOrder
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      } else {
        // Compare other types
        if (aValue < bValue) {
          return this.sortOrder ? -1 : 1;
        }
        if (aValue > bValue) {
          return this.sortOrder ? 1 : -1;
        }
        return 0;
      }
    });
  }

  public resolvePath(object: any, path: string) {
    return path.split('.').reduce((o, p) => (o ? o[p] : ''), object);
  }
}
